import { Menu, X } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import logoIcon from "@/assets/logo-icon-white.png";
import LanguageSelector from "@/components/LanguageSelector";

const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const headerRef = useRef<HTMLElement>(null);

  const navItems = [
    { label: t("nav.services"), href: "#servicos" },
    { label: t("nav.aboutUs"), href: "/sobrenos" },
    { label: t("nav.testimonials"), href: "#depoimentos" },
    { label: t("nav.clients"), href: "#clientes" },
    { label: t("nav.contact"), href: "#contato" },
  ];

  // Close menu on any touch/click outside the header
  useEffect(() => {
    if (!mobileOpen) return;
    const handleOutsideClick = (e: MouseEvent | TouchEvent) => {
      if (headerRef.current && !headerRef.current.contains(e.target as Node)) {
        setMobileOpen(false);
      }
    };
    document.addEventListener("pointerdown", handleOutsideClick);
    return () => document.removeEventListener("pointerdown", handleOutsideClick);
  }, [mobileOpen]);

  const handleAnchorClick = (e: React.MouseEvent, href: string) => {
    e.preventDefault();
    setMobileOpen(false);

    if (location.pathname === "/") {
      const el = document.querySelector(href);
      if (el) {
        el.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      navigate("/" + href);
    }
  };

  const linkClass = "text-sm font-body font-medium text-muted-foreground hover:text-primary transition-colors duration-150 ease-project";

  return (
    <header ref={headerRef} className="fixed top-0 left-0 right-0 z-50 glass border-b border-primary/10">
      <div className="mx-auto max-w-7xl px-6 md:px-8 flex items-center justify-between h-16">
        <Link to="/" className="flex items-center">
          <img src={logoIcon} alt="Freela Media" className="h-14" />
        </Link>
        <div className="hidden lg:flex items-center gap-8">
          <nav className="flex items-center gap-8">
            {navItems.map((item) =>
              item.href.startsWith("/") ? (
                <Link key={item.label} to={item.href} className={linkClass}>
                  {item.label}
                </Link>
              ) : (
                <a
                  key={item.label}
                  href={item.href}
                  onClick={(e) => handleAnchorClick(e, item.href)}
                  className={linkClass}
                >
                  {item.label}
                </a>
              )
            )}
          </nav>
          <div className="w-px h-5 bg-primary/20" />
          <LanguageSelector />
        </div>
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className="lg:hidden text-foreground p-2 -mr-2"
          aria-label="Menu"
        >
          {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {mobileOpen && (
        <div className="lg:hidden glass border-t border-primary/10 px-6 pb-6 pt-4 space-y-4">
          {navItems.map((item) =>
            item.href.startsWith("/") ? (
              <Link
                key={item.label}
                to={item.href}
                onClick={() => setMobileOpen(false)}
                className={`block ${linkClass}`}
              >
                {item.label}
              </Link>
            ) : (
              <a
                key={item.label}
                href={item.href}
                onClick={(e) => handleAnchorClick(e, item.href)}
                className={`block ${linkClass}`}
              >
                {item.label}
              </a>
            )
          )}
          <div className="pt-2 border-t border-primary/10">
            <LanguageSelector />
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
